/**
 * Safe wrappers around PostHog client methods.
 *
 * All functions are SSR-safe no-ops. Import these anywhere; they will
 * silently skip on the server or when the kill-switch is off.
 *
 * Prefer these over calling posthog-js directly so that:
 *  - We can swap providers or add sampling in one place
 *  - Bundle stays lean (no top-level posthog-js import)
 *  - Errors in analytics NEVER break the app
 */

import { getPosthog, initPosthog, isPosthogEnabled } from "./posthog-client";

function safe(fn) {
  return async (...args) => {
    if (!isPosthogEnabled()) return;
    try {
      let ph = getPosthog();
      if (!ph) ph = await initPosthog();
      if (!ph) return;
      return fn(ph, ...args);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[analytics] error:", err);
      }
    }
  };
}

/** Capture a custom event. */
export const track = safe((ph, event, properties = {}) => {
  ph.capture(event, properties);
});

/**
 * Identify the current user. Call after login.
 * `distinctId` should be the stable user id (e.g. session.user.sub).
 * `properties` become "person properties" (searchable in PostHog).
 */
export const identify = safe((ph, distinctId, properties = {}) => {
  ph.identify(distinctId, properties);
});

/**
 * Alias the current anonymous id to the user id.
 * Call this ONCE at signup — merges the pre-signup guest journey into
 * the authenticated user profile. `identify()` also does this
 * automatically in most cases; keep alias() for the signup moment.
 */
export const alias = safe((ph, distinctId) => {
  if (typeof ph.get_distinct_id === "function") {
    const currentDistinctId = ph.get_distinct_id();
    if (currentDistinctId === distinctId) return;
  }

  ph.alias(distinctId);
});

/** Reset the current user context (call on logout). */
export const reset = safe((ph) => {
  ph.reset();
});

/**
 * Group analytics. e.g. group("provider", providerId, { name, plan })
 * Enables analyzing metrics grouped by provider/team/organization.
 */
export const group = safe((ph, groupType, groupKey, properties = {}) => {
  ph.group(groupType, groupKey, properties);
});

/** Update person properties without a new identify call. */
export const setPersonProperties = safe((ph, properties) => {
  ph.setPersonProperties(properties);
});

/**
 * Track revenue — feeds PostHog's revenue analytics + LTV.
 * Always send in the smallest currency unit is NOT required; use decimals.
 * Example: revenue(29.99, "USD", { plan: "pro", provider: "stripe" })
 */
export const revenue = safe((ph, amount, currency = "USD", extra = {}) => {
  ph.capture("$revenue", {
    revenue: amount,
    currency,
    ...extra,
  });
});

/** Register global properties auto-attached to every event. */
export const registerGlobals = safe((ph, properties) => {
  ph.register(properties);
});

/** Register a one-time property (single event only). */
export const registerOnce = safe((ph, properties) => {
  ph.register_once(properties);
});

/** Manually capture an exception. */
export const captureException = safe((ph, error, extra = {}) => {
  ph.captureException(error, extra);
});

/** Opt current user out of tracking. */
export const optOut = safe((ph) => ph.opt_out_capturing());

/** Opt current user back in. */
export const optIn = safe((ph) => ph.opt_in_capturing());

/** Start session replay capture for the current browser session. */
export const startSessionRecording = safe((ph) => {
  if (typeof ph.startSessionRecording === "function") {
    ph.startSessionRecording();
  }
});

/** Stop session replay capture without disabling all analytics events. */
export const stopSessionRecording = safe((ph) => {
  if (typeof ph.stopSessionRecording === "function") {
    ph.stopSessionRecording();
  }
});
